/**
 * Tests (Quizzes) API — typed wrapper around the existing /testing/quizes backend.
 * Mirrors the legacy TestsAPI.js from patient-2021-web-app-main.
 */
import apiClient from './client';
import type { Test, TestAnswer } from './types';

/**
 * Normalize a legacy question to the modern answerType format.
 * Legacy: { type: 'choice', options: [...] }
 * Modern: { answerType: 'select', selectOptions: [...] }
 */
function normalizeQuestion(q: any): any {
  // Normalize wrong answerType values saved by old ANSWER_TYPES list
  const LEGACY_TYPE_MAP: Record<string, string> = {
    'gap':     'input_gaps',
    'gap_dnd': 'dnd_gaps',
    'stress':  'emphasis',
  };
  if (q.answerType && LEGACY_TYPE_MAP[q.answerType]) {
    q = { ...q, answerType: LEGACY_TYPE_MAP[q.answerType] };
  }

  if (q.answerType) return q; // already modern format

  const mapped = { ...q };
  if (q.type === 'choice') {
    mapped.answerType = 'select';
    if (q.options && !q.selectOptions) {
      mapped.selectOptions = q.options;
    }
  } else if (q.type === 'textinput') {
    mapped.answerType = 'typing';
  } else if (q.type === 'record') {
    mapped.answerType = 'speaking';
  } else if (q.type === 'html') {
    mapped.answerType = 'none';
    if (q.html && !q.text) mapped.text = q.html;
  } else if (q.type === 'image') {
    mapped.answerType = 'none';
    if (q.url && !q.imageUrl) mapped.imageUrl = q.url;
  } else {
    // If answerType is set by the new editor, keep it
    mapped.answerType = mapped.answerType || 'none';
  }
  return mapped;
}

/** Normalize all questions in a test */
function normalizeTest(test: any): Test {
  // Extract timeLimit + deadline from testSettings JSON (new format) or direct fields (legacy)
  let extraSettings: { timeLimit?: number; deadline?: string } = {};
  if (test?.testSettings) {
    try { extraSettings = JSON.parse(test.testSettings); } catch {}
  }
  // Server may return questions as 'questionnaireQuestions' (legacy field name)
  const rawQuestions = test.questions || test.questionnaireQuestions || [];
  return {
    ...test,
    timeLimit: extraSettings.timeLimit ?? test.timeLimit,
    deadline: extraSettings.deadline ?? test.deadline,
    questions: rawQuestions.map(normalizeQuestion),
    // Ensure questionCount is always set for list-view counter
    questionCount: test.questionCount ?? rawQuestions.length,
  };
}

const TestsAPI = {
  /** Get all tests for current user */
  async getAllTests(): Promise<Test[]> {
    try {
      const { data } = await apiClient.get<any>(
        '/testing/quizes/all',
      );
      const list = Array.isArray(data) ? data : (data?.result || []);
      return (Array.isArray(list) ? list : []).map(normalizeTest);
    } catch (err) {
      console.warn('[TestsAPI] getAllTests error:', err);
      return [];
    }
  },

  /** Get publicly accessible tests for student browse */
  async getPublicTests(): Promise<Test[]> {
    try {
      const { data } = await apiClient.get<Test[]>(
        '/testing/quizes/all',
        { params: { access: 'public' } },
      );
      const all = data ?? [];
      return all
        .filter(t => (t as any).access === 'public' || (t as any).access === 'shared' || !(t as any).access)
        .map(normalizeTest);
    } catch {
      return [];
    }
  },

  /** Get a single test by ID (with questions) */
  async getTest(id: string): Promise<Test> {
    const { data } = await apiClient.get<Test>(
      `/testing/quize/${id}`,
    );
    return normalizeTest(data);
  },

  /** Create a new test */
  async createTest(payload: Partial<Test>): Promise<Test> {
    const { data } = await apiClient.post<Test>(
      '/testing/quizes',
      payload,
    );
    return normalizeTest(data);
  },

  /** Update a test */
  async updateTest(payload: Partial<Test> & { id: string }): Promise<Test> {
    const { data } = await apiClient.put<Test>(
      '/testing/quizes',
      payload,
    );
    return normalizeTest(data);
  },

  /** Delete a test (moves to trash) */
  async deleteTest(id: string): Promise<void> {
    await apiClient.delete('/testing/quizes', { data: { id } });
  },

  /** Get tests in trash */
  async getTrash(): Promise<Test[]> {
    const { data } = await apiClient.get<Test[]>('/testing/quizes/trash');
    return data ?? [];
  },

  /** Restore a test from trash */
  async restore(id: string): Promise<void> {
    await apiClient.post(`/testing/quizes/${id}/restore`, {});
  },

  /** Permanently delete a test */
  async permanentDelete(id: string): Promise<void> {
    await apiClient.delete(`/testing/quizes/${id}/permanent`);
  },

  /** Clone a test — creates a copy with '(копия)' appended to the name */
  async cloneTest(id: string): Promise<Test> {
    const original = await this.getTest(id);
    const { id: _id, objectId: _oid, createdAt: _ca, updatedAt: _ua, timestamp: _ts, ...rest } = original as any;
    return this.createTest({ ...rest, name: `${original.name ?? ''} (копия)` });
  },

  /** Save an answer for a question within a test */
  async saveAnswer(
    testId: string,
    cardId: string,
    answer: string,
    isCorrect?: boolean,
  ): Promise<TestAnswer> {
    const { data } = await apiClient.post<TestAnswer>(
      `/v3/exercises/${testId}/answers/card/${cardId}`,
      { answer, isCorrect },
    );
    return data;
  },

  /** Get all answers for a test (optionally for a specific user) */
  async getAnswers(testId: string, userId?: string): Promise<TestAnswer[]> {
    const { data } = await apiClient.get<TestAnswer[]>(
      `/v3/exercises/${testId}/answers`,
      { params: userId ? { userId } : {} },
    );
    return data ?? [];
  },

  /** Grade a question manually (teacher sets score + comment) */
  async gradeManually(
    testId: string,
    cardId: string,
    manualMark: number,
    teacherComment?: string,
  ): Promise<TestAnswer> {
    const { data } = await apiClient.post<TestAnswer>(
      `/v3/exercises/${testId}/answers/card/${cardId}`,
      { answer: 'manual_grade', manualMark, teacherComment },
    );
    return data;
  },
  /**
   * Save a student's full test submission (answers + results) for teacher review.
   * 1. localStorage  — instant, same-device.
   * 2. MongoDB       — primary cross-device store (/v3/test-submissions).
   */
  async saveStudentAnswers(
    testId: string,
    taskId: string,
    userId: string,
    answers: Record<string, any>,
    results: Record<string, { correct: boolean; score: number }>,
  ): Promise<void> {
    const payload = { answers, results, submittedAt: new Date().toISOString() };

    // 1. localStorage — instant, never fails
    try {
      localStorage.setItem(`ep_test_sub_${testId}_${taskId}_${userId}`, JSON.stringify(payload));
    } catch { /* storage full */ }

    // 2. MongoDB — primary cross-device store (works even without Parse)
    try {
      await apiClient.post('/v3/test-submissions', { testId, taskId, userId, ...payload });
    } catch { /* non-fatal */ }

  },

  /**
   * Load a student's saved test submission for teacher review.
   * 1. localStorage — same-device (fastest).
   * 2. MongoDB      — primary cross-device store.
   */
  async getStudentAnswers(
    testId: string,
    taskId: string,
    userId: string,
  ): Promise<{ answers: Record<string, any>; results: Record<string, any>; submittedAt?: string } | null> {
    // 1. Try localStorage (teacher might be on same device, or student just submitted)
    try {
      const raw = localStorage.getItem(`ep_test_sub_${testId}_${taskId}_${userId}`);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }

    // 2. Try MongoDB — primary cross-device store
    try {
      const res = await apiClient.get<{
        submission?: { answers: Record<string, any>; results: Record<string, any>; submittedAt?: string } | null;
        result?: Array<{ answers: Record<string, any>; results: Record<string, any>; submittedAt?: string }>;
      }>(
        '/v3/test-submissions',
        { params: { testId, taskId, userId } },
      );
      // Format A: { submission: { answers, results } } — new format
      if (res.data?.submission?.answers) return res.data.submission;
      // Format B: { result: [...] } — current backend format, take latest entry
      if (Array.isArray(res.data?.result) && res.data.result.length > 0) {
        const sorted = [...res.data.result].sort((a, b) => {
          const ta = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
          const tb = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
          return tb - ta;
        });
        const latest = sorted[0];
        if (latest?.answers) return latest;
      }
    } catch { /* not available */ }

    return null;
  },
};

export default TestsAPI;
